import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  RevokeTokenCommand,
  RespondToAuthChallengeCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  SetUserMFAPreferenceCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  type AuthenticationResultType,
} from '@aws-sdk/client-cognito-identity-provider';
import { getConfigValue } from './configService';
import { readLastInput, recordInput } from './session/activity';
import { finishReauth, isReauthRequested, ReauthRestart } from './session/reauth';
import { currentLimits, sessionState, type EndReason, type SessionState } from './session/limits';
import {
  broadcastLink,
  clearTabStorage,
  endedReason,
  keepReason,
  markEnded,
  onEndedMark,
  type TabLink,
  type TabMessage,
} from './session/tabs';

export interface User {
  username: string;
  email: string;
  groups: string[];
  attributes: Record<string, string>;
}

export interface AuthSession {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
  user: User;
  /** This sign-in's id. Every tab that holds the session shares it. */
  sid: string;
  /** When the person signed in, by this machine's clock. The session ends 12 hours later. */
  signedInAt: number;
}

export interface PasswordChangeRequired {
  requiresPasswordChange: true;
  session: string;
  username: string;
}

export interface MfaSetupRequired {
  requiresMfaSetup: true;
  session: string;
  username: string;
  secretCode: string;
  otpauthUrl: string;
}

export interface MfaCodeRequired {
  requiresMfaCode: true;
  session: string;
  username: string;
}

/** The part of the browser's Storage the session copy uses. */
export type TabStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface AuthServiceDeps {
  now: () => number;
  /** A full page load: drops every credential and secret the page holds in memory. */
  reload: () => void;
  link: TabLink<AuthSession>;
  /**
   * This tab's own storage: a reload keeps it, and closing the tab removes it.
   * Null where the browser refuses it: the session is then in memory only.
   */
  tabStorage: TabStorage | null;
  /** How long a new tab waits for an open tab to hand over the session. */
  handOffWaitMs: number;
  /** How long a sign-out waits for the network before it gives up on revoking. */
  onlineWaitMs: number;
  /** How long one revoke call may take. */
  revokeTimeoutMs: number;
  /**
   * Take a session at start: this tab's own copy, else one from an open tab.
   * Off on the public password pages: a session there counts no input and
   * would reload the form at a sign-out.
   */
  takeSession: boolean;
}

const PUBLIC_PATHS = ['/create-password'];

/** Before TASK-637 the whole session, refresh token included, sat here on disk. */
const LEGACY_STORAGE_KEY = 'auth_session';

/** This tab's copy of the session, in sessionStorage. `clearTabStorage()` removes it too. */
export const TAB_SESSION_KEY = 'portal_session';

/** This tab's sessionStorage, or null where the browser refuses it. */
function defaultTabStorage(): TabStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** A stored session copy, or null when the value is not a whole session. */
function parseStoredSession(raw: string | null): AuthSession | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const session = value as Partial<AuthSession>;
  const user = session.user as Partial<User> | undefined;
  const whole =
    typeof session.accessToken === 'string' &&
    typeof session.idToken === 'string' &&
    typeof session.refreshToken === 'string' &&
    typeof session.expiresAt === 'number' &&
    typeof session.sid === 'string' &&
    typeof session.signedInAt === 'number' &&
    !!user &&
    typeof user === 'object' &&
    typeof user.username === 'string' &&
    typeof user.email === 'string' &&
    Array.isArray(user.groups) &&
    !!user.attributes &&
    typeof user.attributes === 'object';
  return whole ? (session as AuthSession) : null;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * The portal sign-in (TASK-649, TASK-637).
 *
 * The session lives in this tab's memory and in this tab's sessionStorage,
 * never in localStorage. A reload keeps it: the page takes the stored copy
 * through the same checks as a hand-off from another tab. A new tab gets it
 * from an open tab. Closing a tab removes its sessionStorage, so the session
 * is gone when the last portal tab closes.
 *
 * A session ends 12 hours after sign-in, or after 2 hours with no input in any
 * portal tab. Every way to read the session checks both limits first, so a
 * poll that runs before the idle timer (for example after a laptop wakes)
 * cannot use a session past its end. `endSession` is the only way a session
 * ends, and it ends it in every tab that holds it.
 */
export class AuthService {
  private client: CognitoIdentityProviderClient;
  private clientId: string;
  /** Read and write it only through `session`, which keeps the tab's stored copy in step. */
  private held: AuthSession | null = null;
  private refreshInFlight: Promise<AuthSession | null> | null = null;
  /** Rises when a session ends, so a refresh already in flight drops its result. */
  private generation = 0;
  private ending: Promise<void> | null = null;
  /** Set when the person submits the sign-in form: a late hand-off is then ignored. */
  private signingIn = false;
  /** Cognito's challenge while the person confirms it is them, between the password and the code. */
  private reauthChallenge: string | null = null;
  private readonly changeListeners = new Set<() => void>();
  private readonly deps: AuthServiceDeps;
  /** Settles once this tab has asked the open tabs for a session. */
  readonly ready: Promise<void>;

  constructor(deps: Partial<AuthServiceDeps> = {}) {
    this.deps = {
      now: deps.now ?? (() => Date.now()),
      reload: deps.reload ?? (() => window.location.reload()),
      link: deps.link ?? broadcastLink<AuthSession>(),
      tabStorage: deps.tabStorage === undefined ? defaultTabStorage() : deps.tabStorage,
      handOffWaitMs: deps.handOffWaitMs ?? 400,
      onlineWaitMs: deps.onlineWaitMs ?? 60_000,
      revokeTimeoutMs: deps.revokeTimeoutMs ?? 10_000,
      takeSession:
        deps.takeSession ??
        !(typeof window !== 'undefined' && PUBLIC_PATHS.some((path) => window.location.pathname.startsWith(path))),
    };
    this.clientId = '';
    this.client = new CognitoIdentityProviderClient({ region: 'us-east-1' });

    // A session from before TASK-637 left its refresh token on disk. Remove it.
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Storage refused: nothing was stored either.
    }

    this.deps.link.listen((message) => this.onTabMessage(message));
    if (typeof window !== 'undefined') onEndedMark(() => this.checkEndedMark());
    // A reload keeps this tab's own copy: take it first, else ask the open tabs.
    if (!this.deps.takeSession || this.restore()) this.ready = Promise.resolve();
    else this.ready = this.askOpenTabs();
  }

  /**
   * The session in memory. Every write goes through the setter, so the tab's
   * stored copy always matches it: set on sign-in, refresh, confirmation and
   * hand-off, removed when the session ends.
   */
  private get session(): AuthSession | null {
    return this.held;
  }

  private set session(value: AuthSession | null) {
    this.held = value;
    const storage = this.deps.tabStorage;
    if (!storage) return;
    try {
      if (value) storage.setItem(TAB_SESSION_KEY, JSON.stringify(value));
      else storage.removeItem(TAB_SESSION_KEY);
    } catch {
      // Storage full or refused: the session stays in memory, and a reload asks
      // the person to sign in. An older copy must not outlive the one in memory.
      try {
        storage.removeItem(TAB_SESSION_KEY);
      } catch {
        // Refused too: nothing can be read back either.
      }
    }
  }

  /**
   * Take the session this tab held before a reload, through the same checks as
   * a hand-off from another tab. A copy that ended in another tab, is past a
   * limit, or is not a whole session, is removed.
   */
  private restore(): boolean {
    const storage = this.deps.tabStorage;
    if (!storage) return false;
    let raw: string | null;
    try {
      raw = storage.getItem(TAB_SESSION_KEY);
    } catch {
      return false;
    }
    if (raw === null) return false;
    const stored = parseStoredSession(raw);
    if (stored && this.adopt(stored)) return true;
    try {
      storage.removeItem(TAB_SESSION_KEY);
    } catch {
      // Refused: the copy is not used either.
    }
    return false;
  }

  private updateConfig(): boolean {
    const region = getConfigValue('AWS_REGION');
    const userPoolId = getConfigValue('USER_POOL_ID');
    const clientId = getConfigValue('USER_POOL_CLIENT_ID');

    if (!region || !userPoolId || !clientId) {
      return false;
    }

    if (this.client.config.region !== region) {
      this.client = new CognitoIdentityProviderClient({ region });
    }

    this.clientId = clientId;
    return true;
  }

  // ---- Who is signed in -------------------------------------------------

  /** Hear when the session starts, changes or ends. Returns the unsubscribe. */
  subscribe(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  /**
   * Tell listeners after the current task: a limit check can end the session
   * while React renders, and a state change inside a render is refused.
   */
  private notify(): void {
    queueMicrotask(() => {
      for (const listener of this.changeListeners) listener();
    });
  }

  /**
   * The signed-in session, inside both limits, whatever the age of its access
   * token. A session past a limit, or ended in another tab, ends here.
   */
  getSignedInSession(): AuthSession | null {
    const session = this.session;
    if (!session) return null;
    const marked = endedReason(session.sid);
    if (marked) {
      void this.endSession(marked, { fromOtherTab: true });
      return null;
    }
    const state = sessionState(this.deps.now(), session.signedInAt, readLastInput(), currentLimits());
    if (state.kind === 'ended') {
      void this.endSession(state.reason);
      return null;
    }
    return session;
  }

  /**
   * The session for a call that sends a token. Null when signed out, and null
   * while the access token waits for its refresh (never a reason to sign out).
   */
  getCurrentSession(): AuthSession | null {
    const session = this.getSignedInSession();
    if (!session || this.deps.now() >= session.expiresAt) return null;
    return session;
  }

  /** Where the session stands against the idle and 12-hour limits, for the warning. */
  getSessionState(): SessionState | null {
    const session = this.getSignedInSession();
    if (!session) return null;
    return sessionState(this.deps.now(), session.signedInAt, readLastInput(), currentLimits());
  }

  /** True from the moment a session starts to end until the page reloads. */
  isEnding(): boolean {
    return this.ending !== null;
  }

  isAuthenticated(): boolean {
    return this.getSignedInSession() !== null;
  }

  getCurrentUser(): User | null {
    return this.getSignedInSession()?.user || null;
  }

  hasRole(role: string): boolean {
    return this.getCurrentUser()?.groups.includes(role) || false;
  }

  // ---- Tabs ----------------------------------------------------------------

  /**
   * Ask the open tabs for the session. The page waits a moment for an answer,
   * then shows the sign-in form. A tab that is busy or slowed down by the
   * browser may answer later: that answer is still taken, until the person
   * submits the sign-in form, so they do not sign in twice.
   */
  private askOpenTabs(): Promise<void> {
    const id = randomId();
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, this.deps.handOffWaitMs);
      const stop = this.deps.link.listen((message) => {
        if (message.type !== 'session' || message.id !== id) return;
        if (this.signingIn) {
          stop();
          return;
        }
        if (this.adopt(message.session)) {
          stop();
          clearTimeout(timer);
          resolve();
        }
      });
      this.deps.link.post({ type: 'request', id });
    });
  }

  /**
   * Take a session another tab handed over, or this tab kept through a reload,
   * if no tab ended it and it is still inside both limits.
   */
  private adopt(session: AuthSession): boolean {
    if (this.session || this.ending) return false;
    if (endedReason(session.sid)) return false;
    const state = sessionState(this.deps.now(), session.signedInAt, readLastInput(), currentLimits());
    if (state.kind === 'ended') return false;
    this.session = session;
    this.notify();
    return true;
  }

  private onTabMessage(message: TabMessage<AuthSession>): void {
    if (message.type === 'request') {
      const session = this.getSignedInSession();
      if (session && !this.ending) this.deps.link.post({ type: 'session', id: message.id, session });
    } else if (message.type === 'ended') {
      // Only the same sign-in. A tab with no session, or a newer one, is not touched.
      if (this.session && this.session.sid === message.sid) {
        void this.endSession(message.reason, { fromOtherTab: true });
      }
    } else if (message.type === 'updated') {
      // The person confirmed it is them in another tab: take the fresh tokens.
      if (this.session && !this.ending && this.session.sid === message.session.sid) {
        this.generation += 1; // a refresh in flight with the old token drops its result
        this.session = message.session;
        this.notify();
        // This tab's own prompt is answered too: its waiting requests go again.
        if (isReauthRequested()) {
          this.reauthChallenge = null;
          finishReauth();
        }
      }
    }
  }

  private checkEndedMark(): void {
    if (!this.session) return;
    const reason = endedReason(this.session.sid);
    if (reason) void this.endSession(reason, { fromOtherTab: true });
  }

  // ---- Start and end -------------------------------------------------------

  private async startSession(result: AuthenticationResultType, missing: string): Promise<AuthSession> {
    const { AccessToken, IdToken, RefreshToken, ExpiresIn } = result;
    if (!AccessToken || !IdToken || !RefreshToken) {
      throw new Error(missing);
    }
    const user = await this.getUserInfo(AccessToken, IdToken);
    const now = this.deps.now();
    const session: AuthSession = {
      accessToken: AccessToken,
      idToken: IdToken,
      refreshToken: RefreshToken,
      expiresAt: now + (ExpiresIn ?? 0) * 1000,
      user,
      sid: randomId(),
      signedInAt: now,
    };
    // Signing in is input. Every open tab sees the fresh time at once.
    recordInput(now, true);
    this.session = session;
    this.notify();
    return session;
  }

  /**
   * End the session in this tab and in every tab that holds it, then reload.
   *
   * The order matters. The session leaves memory and this tab's storage first,
   * so no refresh, call or reload can use it. The sign-in is marked as ended
   * before other tabs are told, so
   * a tab that misses the message still finds out. The revoke runs last
   * because it needs the network. The reload drops every credential and
   * secret still in the page's memory.
   */
  endSession(reason: EndReason, opts: { fromOtherTab?: boolean } = {}): Promise<void> {
    if (this.ending) return this.ending;
    const ended = this.session;
    this.generation += 1;
    this.session = null;
    this.ending = (async () => {
      keepReason(reason);
      if (ended && !opts.fromOtherTab) {
        markEnded(ended.sid, reason, this.deps.now());
        this.deps.link.post({ type: 'ended', sid: ended.sid, reason });
      }
      this.notify();
      // An expired session has nothing left to revoke. Another tab revokes its own.
      if (ended && !opts.fromOtherTab && reason !== 'expired') {
        await this.revoke(ended);
      }
      // Last, because the portal settings the revoke needs live in sessionStorage too.
      clearTabStorage();
      this.deps.reload();
    })();
    return this.ending;
  }

  /**
   * Sign the person out everywhere: every refresh, access and ID token they
   * hold, on every computer. The Sign out button and an automatic end (idle,
   * 12 hours) do the same (TASK-649). Signing out everywhere also stops the
   * identity pool giving AWS credentials to a copied ID token; revoking one
   * refresh token would not. RevokeToken is the fallback when no access token
   * can be had.
   */
  private async revoke(ended: AuthSession): Promise<void> {
    if (!this.updateConfig()) {
      console.error('[auth] Sign-out could not revoke the session: the portal settings are not loaded');
      return;
    }
    const attempt = async () => {
      // Everywhere needs a live access token. After a wake it may have expired: refresh it first.
      let accessToken = ended.accessToken;
      if (this.deps.now() >= ended.expiresAt) {
        const refreshed = await withTimeout(
          this.client.send(
            new InitiateAuthCommand({
              AuthFlow: 'REFRESH_TOKEN_AUTH',
              ClientId: this.clientId,
              AuthParameters: { REFRESH_TOKEN: ended.refreshToken },
            })
          ),
          this.deps.revokeTimeoutMs
        );
        accessToken = refreshed.AuthenticationResult?.AccessToken ?? '';
      }
      if (accessToken) {
        await withTimeout(
          this.client.send(new GlobalSignOutCommand({ AccessToken: accessToken })),
          this.deps.revokeTimeoutMs
        );
        return;
      }
      await withTimeout(
        this.client.send(new RevokeTokenCommand({ ClientId: this.clientId, Token: ended.refreshToken })),
        this.deps.revokeTimeoutMs
      );
    };

    for (let round = 0; round < 2; round += 1) {
      // A laptop that wakes after a night has no network for a moment. That is
      // when most idle sign-outs run, and the token is only in memory, so wait.
      if (!navigator.onLine && !(await this.waitForOnline(this.deps.onlineWaitMs))) break;
      try {
        await attempt();
        return;
      } catch (error) {
        if (this.isPermanentAuthError(error)) return; // Cognito already refuses the token.
        if (!this.isTransientError(error)) {
          console.error('[auth] Sign-out could not revoke the session:', error);
          return;
        }
      }
    }
    console.error('[auth] Sign-out could not reach Cognito; the refresh token stays valid until it expires');
  }

  // ---- Sign-in ---------------------------------------------------------------

  private decodeJwt(token: string): Record<string, unknown> | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  private async buildMfaSetupRequired(session: string, username: string): Promise<MfaSetupRequired> {
    const assoc = await this.client.send(
      new AssociateSoftwareTokenCommand({
        Session: session,
      })
    );

    const secret = assoc.SecretCode;
    if (!secret) {
      throw new Error('Failed to initiate MFA setup');
    }

    const nextSession = assoc.Session || session;
    const issuer = 'Arcanum Customer Success Portal';
    const label = encodeURIComponent(`${issuer}:${username}`);
    const otpauthUrl = `otpauth://totp/${label}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;

    return {
      requiresMfaSetup: true,
      session: nextSession,
      username,
      secretCode: secret,
      otpauthUrl,
    };
  }

  async signIn(
    email: string,
    password: string
  ): Promise<AuthSession | PasswordChangeRequired | MfaSetupRequired | MfaCodeRequired> {
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }
    this.signingIn = true;

    try {
      const response = await this.client.send(
        new InitiateAuthCommand({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: this.clientId,
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password,
          },
        })
      );

      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          requiresPasswordChange: true,
          session: response.Session!,
          username: email,
        };
      }

      // MFA setup required (no device enrolled yet)
      if (response.ChallengeName === 'MFA_SETUP') {
        return await this.buildMfaSetupRequired(response.Session!, email);
      }

      // MFA required (user already enrolled)
      if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        return {
          requiresMfaCode: true,
          session: response.Session!,
          username: email,
        };
      }

      if (!response.AuthenticationResult) {
        throw new Error('Authentication failed');
      }
      return await this.startSession(response.AuthenticationResult, 'Incomplete authentication response');
    } catch (error) {
      console.error('Sign in error:', error);
      throw new Error('Invalid email or password');
    }
  }

  async completeMfaSetupAndSignIn(username: string, session: string, code: string): Promise<AuthSession> {
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }

    try {
      const verify = await this.client.send(
        new VerifySoftwareTokenCommand({
          Session: session,
          UserCode: code,
          FriendlyDeviceName: 'Arcanum CSP',
        })
      );

      if (verify.Status !== 'SUCCESS') {
        throw new Error('Invalid verification code');
      }

      const nextSession = verify.Session || session;

      // Complete the MFA setup challenge, choosing software token as the factor
      const finalize = await this.client.send(
        new RespondToAuthChallengeCommand({
          ChallengeName: 'MFA_SETUP',
          ClientId: this.clientId,
          Session: nextSession,
          ChallengeResponses: {
            USERNAME: username,
            ANSWER: 'SOFTWARE_TOKEN_MFA',
          },
        })
      );

      if (!finalize.AuthenticationResult) {
        throw new Error('MFA setup completion failed');
      }

      const accessToken = finalize.AuthenticationResult.AccessToken;
      if (accessToken) {
        try {
          await this.client.send(
            new SetUserMFAPreferenceCommand({
              AccessToken: accessToken,
              SoftwareTokenMfaSettings: {
                Enabled: true,
                PreferredMfa: true,
              },
            })
          );
        } catch (setMfaError) {
          console.warn('Failed to set software token MFA preference:', setMfaError);
        }
      }

      return await this.startSession(
        finalize.AuthenticationResult,
        'Incomplete authentication response after MFA setup'
      );
    } catch (error) {
      console.error('MFA setup error:', error);
      throw error instanceof Error ? error : new Error('Failed to complete MFA setup');
    }
  }

  async respondToMfaCodeAndSignIn(username: string, session: string, code: string): Promise<AuthSession> {
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }
    try {
      const challenge = await this.client.send(
        new RespondToAuthChallengeCommand({
          ChallengeName: 'SOFTWARE_TOKEN_MFA',
          ClientId: this.clientId,
          Session: session,
          ChallengeResponses: {
            USERNAME: username,
            SOFTWARE_TOKEN_MFA_CODE: code,
          },
        })
      );

      if (!challenge.AuthenticationResult) {
        throw new Error('Invalid MFA code');
      }
      return await this.startSession(challenge.AuthenticationResult, 'Incomplete authentication response after MFA');
    } catch (error) {
      console.error('MFA code error:', error);
      throw error instanceof Error ? error : new Error('Failed to verify MFA code');
    }
  }

  async completePasswordChange(
    session: string,
    username: string,
    newPassword: string,
    fullName: string
  ): Promise<AuthSession | MfaSetupRequired | MfaCodeRequired> {
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }

    try {
      const response = await this.client.send(
        new RespondToAuthChallengeCommand({
          ChallengeName: 'NEW_PASSWORD_REQUIRED',
          ClientId: this.clientId,
          Session: session,
          ChallengeResponses: {
            USERNAME: username,
            NEW_PASSWORD: newPassword,
            'userAttributes.name': fullName, // Provide the required name attribute
          },
        })
      );

      if (response.ChallengeName === 'MFA_SETUP') {
        return await this.buildMfaSetupRequired(response.Session!, username);
      }

      if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        return {
          requiresMfaCode: true,
          session: response.Session!,
          username,
        };
      }

      if (!response.AuthenticationResult) {
        throw new Error('Password change failed');
      }
      return await this.startSession(
        response.AuthenticationResult,
        'Incomplete authentication response after password change'
      );
    } catch (error) {
      console.error('Password change error:', error);
      throw new Error('Failed to change password. Please try again.');
    }
  }

  /** The Sign out button: signs the person out everywhere, in every tab. */
  async signOut(): Promise<void> {
    if (!this.session) return;
    await this.endSession('manual');
  }

  // ---- Confirm it is you (TASK-649) ------------------------------------------

  /**
   * First step of confirming it is the signed-in person, before a destructive
   * operation: their password. Returns 'code' when Cognito asks for the
   * authenticator code next (always, while MFA is required).
   */
  async confirmPassword(password: string): Promise<'code' | 'done'> {
    const session = this.getSignedInSession();
    if (!session) throw new Error('Sign in to the portal again.');
    if (!this.updateConfig()) throw new Error('Configuration not loaded. Please refresh the page.');
    let response;
    try {
      response = await this.client.send(
        new InitiateAuthCommand({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: this.clientId,
          AuthParameters: { USERNAME: session.user.username, PASSWORD: password },
        })
      );
    } catch (error) {
      throw this.confirmError(error, 'The password is not correct.');
    }
    if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA' && response.Session) {
      this.reauthChallenge = response.Session;
      return 'code';
    }
    if (response.AuthenticationResult) {
      await this.applyReauth(response.AuthenticationResult);
      return 'done';
    }
    throw new Error('Sign out, then sign in again to confirm it is you.');
  }

  /** Second step: the code the person's authenticator app shows now. */
  async confirmCode(code: string): Promise<void> {
    const session = this.getSignedInSession();
    if (!session || !this.reauthChallenge) throw new Error('Enter your password again.');
    let response;
    try {
      response = await this.client.send(
        new RespondToAuthChallengeCommand({
          ChallengeName: 'SOFTWARE_TOKEN_MFA',
          ClientId: this.clientId,
          Session: this.reauthChallenge,
          ChallengeResponses: { USERNAME: session.user.username, SOFTWARE_TOKEN_MFA_CODE: code },
        })
      );
    } catch (error) {
      const name = error instanceof Error ? error.name : '';
      if (name === 'CodeMismatchException') {
        throw new Error('The code is not correct. Enter the code your authenticator app shows now.');
      }
      if (name === 'NotAuthorizedException' || name === 'ExpiredCodeException') {
        // The challenge expired: start again from the password.
        this.reauthChallenge = null;
        throw new ReauthRestart();
      }
      throw this.confirmError(error, 'The code is not correct. Enter the code your authenticator app shows now.');
    }
    if (!response.AuthenticationResult) throw new Error('Sign out, then sign in again to confirm it is you.');
    this.reauthChallenge = null;
    await this.applyReauth(response.AuthenticationResult);
  }

  /** The message for a failed confirmation step. */
  private confirmError(error: unknown, wrong: string): Error {
    const name = error instanceof Error ? error.name : '';
    if (name === 'ForbiddenException') {
      // The user pool's web ACL: an address outside NZ and Australia, or too many sign-in steps.
      return new Error('The sign-in service refused this network. Wait 5 minutes, then try again.');
    }
    if (this.isTransientError(error)) return new Error('The portal could not reach the sign-in service. Try again.');
    return new Error(wrong);
  }

  /** Forget a half-finished confirmation (the person closed the prompt). */
  cancelConfirm(): void {
    this.reauthChallenge = null;
  }

  /**
   * Use the tokens from a confirmation in place of this sign-in's tokens, in
   * every tab. A confirmation is a new sign-in, so the 12 hours start again.
   * The old refresh token is not revoked: another tab may be refreshing with
   * it at this moment. It ends at its own 12 hours.
   */
  private async applyReauth(result: AuthenticationResultType): Promise<void> {
    const current = this.session;
    const { AccessToken, IdToken, RefreshToken, ExpiresIn } = result;
    if (!current || this.ending) throw new Error('Sign in to the portal again.');
    if (!AccessToken || !IdToken || !RefreshToken)
      throw new Error('Sign out, then sign in again to confirm it is you.');
    const user = await this.getUserInfo(AccessToken, IdToken);
    if (this.session?.sid !== current.sid || this.ending) {
      // The sign-in ended while the person confirmed. The new refresh token
      // must not stay valid with nobody holding it.
      void this.client
        .send(new RevokeTokenCommand({ ClientId: this.clientId, Token: RefreshToken }))
        .catch((error) => console.error('[auth] Could not revoke the confirmation token:', error));
      throw new Error('Sign in to the portal again.');
    }
    const now = this.deps.now();
    const updated: AuthSession = {
      ...current,
      accessToken: AccessToken,
      idToken: IdToken,
      refreshToken: RefreshToken,
      expiresAt: now + (ExpiresIn ?? 0) * 1000,
      user,
      signedInAt: now,
    };
    recordInput(now, true);
    this.generation += 1; // a refresh in flight with the old token drops its result
    this.session = updated;
    this.notify();
    this.deps.link.post({ type: 'updated', session: updated });
  }

  private async getUserInfo(accessToken: string, idToken: string): Promise<User> {
    const response = await this.client.send(
      new GetUserCommand({
        AccessToken: accessToken,
      })
    );

    const attributes: Record<string, string> = {};
    response.UserAttributes?.forEach((attr) => {
      if (attr.Name && attr.Value) {
        attributes[attr.Name] = attr.Value;
      }
    });

    // Groups from the ID token that came with this access token, else from the access token.
    const idGroups = this.extractGroupsFromIdToken(idToken);
    const groups = idGroups.length > 0 ? idGroups : this.extractGroupsFromAccessToken(accessToken);

    return {
      username: response.Username || 'unknown',
      email: attributes['email'] || 'unknown@example.com',
      groups,
      attributes,
    };
  }

  private extractGroupsFromAccessToken(accessToken: string): string[] {
    const payload = this.decodeJwt(accessToken);
    return (payload && (payload['cognito:groups'] as string[])) || [];
  }

  private extractGroupsFromIdToken(idToken: string): string[] {
    const payload = this.decodeJwt(idToken);
    if (!payload) return [];
    // Prefer principal tags if present (alignment with numa-frontend), else fall back to cognito:groups
    const awsTags =
      (payload['https://aws.amazon.com/tags'] as { principal_tags?: { Groups?: unknown } } | undefined) || null;
    const principalGroups = awsTags?.principal_tags?.Groups;
    if (Array.isArray(principalGroups) && principalGroups.length > 0) return principalGroups;
    return (payload['cognito:groups'] as string[]) || [];
  }

  // ---- Refresh -------------------------------------------------------------

  /**
   * A failure worth trying again: no network (each browser words it
   * differently: Chrome "Failed to fetch", Firefox "NetworkError", Safari
   * "Load failed"), a timeout, throttling, or a server error.
   */
  private isTransientError(error: unknown): boolean {
    if (!navigator.onLine) return true;
    const err = error as { name?: string; message?: string; $metadata?: { httpStatusCode?: number } } | null;
    const status = err?.$metadata?.httpStatusCode;
    if (typeof status === 'number' && (status >= 500 || status === 429)) return true;
    const lower = `${err?.name ?? ''} ${err?.message ?? String(error)}`.toLowerCase();
    const transientPatterns = [
      'failed to fetch',
      'fetch failed',
      'networkerror',
      'network',
      'dns',
      'timeout',
      'timed out',
      'aborted',
      'econnrefused',
      'econnreset',
      'enotfound',
      'load failed',
      'configuration not loaded',
      'toomanyrequests',
      'throttl',
      'rate exceeded',
      'internalerror',
      'serviceunavailable',
    ];
    return transientPatterns.some((p) => lower.includes(p));
  }

  private isPermanentAuthError(error: unknown): boolean {
    const name = error instanceof Error ? error.name : '';
    const message = error instanceof Error ? error.message : String(error);
    const lower = `${name} ${message}`.toLowerCase();
    return (
      lower.includes('notauthorizedexception') ||
      lower.includes('usernotfoundexception') ||
      lower.includes('invalid refresh token') ||
      lower.includes('refresh token has been revoked') ||
      lower.includes('refresh token has expired')
    );
  }

  private waitForOnline(timeoutMs = 10000): Promise<boolean> {
    if (navigator.onLine) return Promise.resolve(true);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        window.removeEventListener('online', handler);
        resolve(navigator.onLine);
      }, timeoutMs);
      const handler = () => {
        clearTimeout(timer);
        window.removeEventListener('online', handler);
        resolve(true);
      };
      window.addEventListener('online', handler);
    });
  }

  async refreshTokens(): Promise<AuthSession | null> {
    const start = this.getSignedInSession();
    if (!start) return null;
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }

    // Reuse in-flight refresh to avoid concurrent refreshes
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const generation = this.generation;
    // The session ended, or a new sign-in replaced it, while the call was out.
    const superseded = () => generation !== this.generation || this.session?.sid !== start.sid;

    const MAX_RETRIES = 3;
    const doRefresh = async (): Promise<AuthSession | null> => {
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          if (!navigator.onLine) {
            const online = await this.waitForOnline();
            if (!online) {
              console.warn('[auth] Still offline after waiting, skipping refresh');
              return this.session;
            }
          }

          const response = await this.client.send(
            new InitiateAuthCommand({
              AuthFlow: 'REFRESH_TOKEN_AUTH',
              ClientId: this.clientId,
              AuthParameters: {
                REFRESH_TOKEN: start.refreshToken,
              },
            })
          );

          if (!response.AuthenticationResult) {
            throw new Error('Token refresh failed');
          }

          const { AccessToken, IdToken, ExpiresIn } = response.AuthenticationResult;
          if (!AccessToken || !IdToken) {
            throw new Error('Incomplete refresh response');
          }

          const user = await this.getUserInfo(AccessToken, IdToken);
          if (superseded()) return this.session;

          const updated: AuthSession = {
            ...start,
            accessToken: AccessToken,
            idToken: IdToken,
            expiresAt: this.deps.now() + (ExpiresIn ?? 0) * 1000,
            user,
          };
          this.session = updated;
          this.notify();
          return updated;
        } catch (error) {
          if (superseded()) return this.session;

          if (this.isPermanentAuthError(error)) {
            console.error('[auth] The session was refused, signing out:', error);
            void this.endSession('expired');
            return null;
          }

          if (this.isTransientError(error) && attempt < MAX_RETRIES - 1) {
            const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
            console.warn(
              `[auth] Transient refresh error (attempt ${attempt + 1}/${MAX_RETRIES}), retrying in ${delay}ms:`,
              error
            );
            await new Promise((r) => setTimeout(r, delay));
            continue;
          }

          // Only Cognito refusing the token ends the session. Anything else keeps
          // it, and the next cycle tries again; the 12-hour limit still applies.
          console.error(`[auth] Refresh failed after ${attempt + 1} attempt(s), keeping the session:`, error);
          return this.session;
        }
      }
      return this.session;
    };

    this.refreshInFlight = doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  async ensureValidSession(minValidityMs = 5 * 60 * 1000): Promise<AuthSession | null> {
    const session = this.getSignedInSession();
    if (!session) return null;
    if (this.deps.now() >= session.expiresAt - Math.max(minValidityMs, 0)) {
      return await this.refreshTokens();
    }
    return session;
  }

  // ---- Password reset --------------------------------------------------------

  async requestPasswordReset(email: string): Promise<void> {
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }

    try {
      await this.client.send(
        new ForgotPasswordCommand({
          ClientId: this.clientId,
          Username: email.toLowerCase(),
        })
      );
    } catch (error) {
      console.error('Password reset request error:', error);
      throw new Error('Failed to request password reset. Please check your email address.');
    }
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    if (!this.updateConfig()) {
      throw new Error('Configuration not loaded. Please refresh the page.');
    }

    try {
      await this.client.send(
        new ConfirmForgotPasswordCommand({
          ClientId: this.clientId,
          Username: email.toLowerCase(),
          ConfirmationCode: code,
          Password: newPassword,
        })
      );
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      throw new Error('Failed to reset password. Please check your code and try again.');
    }
  }
}

export const authService = new AuthService();
